import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FiClipboard, FiClock, FiPlay, FiPlus, FiSettings } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useSurveyStatus } from "../../dashboard/hooks/useSurveyStatus";

export function SurveyListPanel({ cardBg, cardTitle, cardMuted, onStartSurvey, onCreateSurvey, onManageSurveys }) {
  const { t } = useTranslation("common");
  const { status, loading } = useSurveyStatus();

  if (loading) return null;

  const inProgress = status?.in_progress || [];
  const available = status?.available_surveys || [];
  const total = inProgress.length + available.length;

  return (
    <Box
      bg={cardBg}
      color={cardTitle}
      borderRadius="md"
      overflow="hidden"
      boxShadow="md"
      p={4}
    >
      <HStack mb={3}>
        <Icon as={FiClipboard} />
        <Text fontWeight="bold" fontSize="md" color={cardTitle}>
          {t("survey.surveys_title", "Surveys")}
        </Text>
        {total > 0 && (
          <Badge colorScheme="gwbi" borderRadius="full" fontSize="0.65em">
            {total}
          </Badge>
        )}
      </HStack>

      <VStack align="stretch" spacing={2}>
        {inProgress.map((s) => (
          <HStack
            key={s.code}
            justify="space-between"
            align="center"
            py={1}
          >
            <VStack spacing={0} align="start" flex={1} minW={0}>
              <HStack spacing={1}>
                <Icon as={FiPlay} color="gwbi.300" boxSize={3} />
                <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                  {s.title || s.code}
                </Text>
              </HStack>
              <Text fontSize="xs" color="gwbi.300">
                {t("survey.progress_label", "{{pct}}% done", { pct: s.progress_pct })}
              </Text>
            </VStack>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gwbi"
              flexShrink={0}
              onClick={() => onStartSurvey?.(s.code)}
            >
              {t("survey.continue", "Continue")}
            </Button>
          </HStack>
        ))}

        {available.map((s) => (
          <HStack
            key={s.code}
            justify="space-between"
            align="center"
            py={1}
          >
            <VStack spacing={0} align="start" flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                {s.title || s.code}
              </Text>
              {s.estimated_minutes && (
                <HStack spacing={1}>
                  <Icon as={FiClock} color={cardMuted} boxSize={3} />
                  <Text fontSize="xs" color={cardMuted}>
                    {t("survey.estimated_time", "~{{min}} minutes", { min: s.estimated_minutes })}
                  </Text>
                </HStack>
              )}
            </VStack>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gwbi"
              flexShrink={0}
              onClick={() => onStartSurvey?.(s.code)}
            >
              {t("survey.start", "Start")}
            </Button>
          </HStack>
        ))}

        {total > 0 && <Divider borderColor="gray.200" />}

        <Button
          size="sm"
          variant="outline"
          colorScheme="gwbi"
          leftIcon={<FiPlus />}
          onClick={() => onCreateSurvey?.()}
          w="100%"
        >
          {t("survey.new", "New Survey")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="gwbi"
          leftIcon={<FiSettings />}
          onClick={() => onManageSurveys?.()}
          w="100%"
        >
          {t("survey.manage", "Manage Surveys")}
        </Button>
      </VStack>
    </Box>
  );
}
